import { ElementType } from "react";

export type TemplateCustomFilter = "for-you" | "new" | "popular";
export const templateCustomFilterMetadata: {
  [type in TemplateCustomFilter]: {
    name: string;
    Icon?: ElementType;
  };
} = {
  "for-you": {
    name: "For You",
    // Icon: UserPlusIcon,
  },
  new: {
    name: "New",
    Icon: () => <span>👀</span>,
  },
  popular: {
    name: "Popular",
    Icon: () => <span>🔥</span>,
  },
};

export const templateTagsFilterMetadata = [
  "MOVIES",
  "BOLLYWOOD",
  "MUSIC VIDEO",
  "SPORTS",
  "MEMES",
  "TIKTOK",
  "TRANSITION",
  "KPOP",
] as const;
export type TemplateTagsFilter = (typeof templateTagsFilterMetadata)[number];
export type TemplateFilter =
  | (typeof typeWithTemplate)[number]
  | TemplateCustomFilter
  | TemplateTagsFilter;
export const typeWithTemplate = [
  "ANIMATION",
  "VIDEO_TO_VIDEO",
  "FACE_SWAP",
  "LIP_SYNC",
  // TODO: enable these once we have more templates
  // "TEXT_TO_VIDEO",
] as const;

type FormatOutput = {
  filter: TemplateFilter;
  pageTitle: string;
  seoTitle: string;
  description: string;
  keywords: string[];
};

type SnakeToKebabCase<S extends string> = S extends `${infer T}${"_" | " "}${infer U}`
  ? `${T}-${SnakeToKebabCase<U>}`
  : S;

export const templateFilterData: {
  [key in SnakeToKebabCase<Lowercase<Exclude<TemplateFilter, "for-you">>>]: FormatOutput;
} = {
  popular: {
    filter: "popular",
    pageTitle: "Popular AI Video Templates",
    seoTitle: "Popular Templates",
    description:
      "Discover what's trending with our popular AI video templates. These templates are community favorites for creating engaging and visually appealing videos.",
    keywords: [],
  },
  new: {
    filter: "new",
    pageTitle: "Latest AI Video Templates",
    seoTitle: "Latest Templates",
    description:
      "Stay ahead of the curve with our newest AI video templates. Explore fresh designs and innovative features to make your video projects stand out from the crowd.",
    keywords: [],
  },
  animation: {
    filter: "ANIMATION",
    pageTitle: "AI Animated Video Templates",
    seoTitle: "AI Animated Video Templates",
    description:
      "Animate your dreams with our AI video templates for animation. Perfect for storytellers, marketers, and educators looking to create engaging animated content.",
    keywords: ["video animation templates", "animation template", "animated video templates"],
  },
  "video-to-video": {
    filter: "VIDEO_TO_VIDEO",
    pageTitle: "AI Video-to-Video Templates",
    seoTitle: "AI Video-to-Video Templates",
    description:
      "Revolutionize your content with AI video-to-video generation. Transform any footage into enhanced, seamless video sequences perfect for professional and personal projects.",
    keywords: [],
  },
  "face-swap": {
    filter: "FACE_SWAP",
    pageTitle: "AI Face Swap Video Templates",
    seoTitle: "AI Face Swap Video Templates",
    description:
      "Innovate your videos with AI face swap technology. Ideal for entertainment, marketing, and personal projects, these templates offer a fun way to engage and surprise your audience.",
    keywords: [],
  },
  "lip-sync": {
    filter: "LIP_SYNC",
    pageTitle: "AI Lip Sync Video Templates",
    seoTitle: "AI Lip Sync Video Templates",
    description:
      "Achieve flawless lip sync in your videos with AI. Our templates ensure your voice-overs match perfectly with on-screen actions, enhancing realism and viewer engagement.",
    keywords: ["lip sync template"],
  },
  "music-video": {
    filter: "MUSIC VIDEO",
    pageTitle: "Music Video Templates",
    seoTitle: "Free Music Video Templates",
    description:
      "Transform your music into a visual spectacle with our AI music video templates. Find the perfect backdrop to complement your sound.",
    keywords: [
      "music video template",
      "music template video",
      "music video templates free download",
    ],
  },
  memes: {
    filter: "MEMES",
    pageTitle: "Meme Video Templates",
    seoTitle: "Meme Video Templates",
    description:
      "Unleash your creativity and go viral with AI-powered meme video templates. These templates provide the perfect canvas for your creations.",
    keywords: [
      "meme template generator",
      "video meme templates",
      "meme video template",
      "meme video templates",
    ],
  },
  movies: {
    filter: "MOVIES",
    pageTitle: "Cinematic Video Templates",
    seoTitle: "Free Cinematic Video Templates",
    description:
      "Elevate your projects with AI-powered cinematic video templates—perfect for filmmakers and creators seeking professional-quality visuals.",
    keywords: ["cinematic video", "cinematic template", "cinematic template free download"],
  },
  sports: {
    filter: "SPORTS",
    pageTitle: "Sports Video & Graphic Templates",
    seoTitle: "Sports Video & Graphic Templates",
    description:
      "Bring the game to life with AI-powered sports graphic templates. Create dynamic highlight reels, event promos, and engaging content.",
    keywords: ["sports templates", "sports graphic templates", "sports graphics templates free"],
  },
  tiktok: {
    filter: "TIKTOK",
    pageTitle: "AI Video Templates for TikTok",
    seoTitle: "Free TikTok Video Templates",
    description:
      "Create viral TikTok videos effortlessly with our AI-powered TikTok video templates designed for maximum engagement and views.",
    keywords: [
      "tiktok templates",
      "tiktok video template",
      "tik tok templates",
      "free tiktok templates",
    ],
  },
  bollywood: {
    filter: "BOLLYWOOD",
    pageTitle: "AI Video Templates from Bollywood",
    seoTitle: "AI Bollywood Video Templates",
    description:
      "Infuse the vibrant spirit of Bollywood into your TikTok creations with our tailored AI video templates. Crafted to embody the essence of Bollywood cinema, these templates promise to enliven your videos and captivate your audience.",
    keywords: ["bollywood template", "bollywood video templates"],
  },
  transition: {
    filter: "TRANSITION",
    pageTitle: "AI Video Templates of Transition Edits",
    seoTitle: "AI Video Templates of Transition Edits",
    description:
      "Enhance your TikTok videos with AI-powered templates for seamless, captivating transitions that boost engagement.",
    keywords: [],
  },
  kpop: {
    filter: "KPOP",
    pageTitle: "AI Video Templates of Kpop",
    seoTitle: "AI Video Templates of Kpop",
    description:
      "Bring the electrifying energy of Kpop to your TikTok videos with our dedicated AI Kpop video templates.",
    keywords: ["kpop templates"],
  },
};

export function getTemplateFilterData(filter: string) {
  if (Object.keys(templateFilterData).includes(filter) === false) {
    return null;
  }

  // safe to cast here because we check if the key exists in the map
  return templateFilterData[filter as keyof typeof templateFilterData];
}

export const templateCollectionName = `templates`;
export const templateVectorSize = 768;
export const templateEmbeddingModel = "text-embedding-3-small";
